import {
    Nav,
    NavFundClass,
    Party,
    PartyFundInfoClass,
    TransactionStatus,
    TransferAgentOrder,
    TransferAgentOrderEnum
} from "../../types.generated";

export interface CashLadderItem {
    name: string;
    currency: string;
    type: string;
    balance: number;
    valueDate: string;
}

export interface NettedOrderValue {
    currency: string;
    valueDate: string;
    nettedOrderValue: number;
}

// Same netting as the Order Summary page, but also grouped per value date: orders are summed across classes, orders given
// in units are valued with the latest NAV per unit, or the class start NAV per unit when the fund has no NAV yet.
// SELL orders are negative. Settled orders are left out: setting the NAV books them as transactions on the cash account,
// so they are already in the cash balances.
export function nettedOrderValues(fund: Partial<Party>, orders: Partial<TransferAgentOrder>[], navs: Partial<Nav>[]): NettedOrderValue[] {
    const fundOrders = orders.filter((o) => o.clientId === fund._id && o.status !== TransactionStatus.Settled);
    const nav = navs.find((n) => n.clientId === fund._id);
    const navByInstrumentId: Record<string, NavFundClass> = {};
    if (nav?.data?.classes) {
        nav.data.classes.forEach((c: NavFundClass) => {
            navByInstrumentId[c.instrumentId] = c;
        });
    }
    const startNavByInstrumentId: Record<string, PartyFundInfoClass> = {};
    if (fund.fundInfo?.classes) {
        fund.fundInfo.classes.forEach((c: PartyFundInfoClass) => {
            startNavByInstrumentId[c.instrumentId] = c;
        });
    }
    const result: NettedOrderValue[] = [];

    fundOrders.forEach((v) => {
        let amount: number = parseFloat(v.amount);
        const units = parseFloat(v.units);
        if (units > 0) {
            let navPerUnit = 0;
            const nfc = navByInstrumentId[v.instrument._id];
            if (nfc) {
                navPerUnit = parseFloat(nfc.navPerUnit);
            }
            if (navPerUnit === 0) {
                const fic = startNavByInstrumentId[v.instrument._id];
                if (fic) {
                    navPerUnit = parseFloat(fic.startNAVPerUnit);
                }
            }
            amount = units * navPerUnit;
        }
        if (v.transactionType === TransferAgentOrderEnum.SELL) {
            amount = amount * -1;
        }

        const existing = result.find((r) => r.currency === v.currency && r.valueDate === v.valueDate);
        if (existing) {
            existing.nettedOrderValue = existing.nettedOrderValue + amount;
        } else {
            result.push({ currency: v.currency, valueDate: v.valueDate, nettedOrderValue: amount });
        }
    });

    return result;
}

// Adds netted order cash to the per-currency balances on and after each order's value date
export const addOrdersToCurrencyBalances = (balances: CashLadderItem[][], orders: NettedOrderValue[]): CashLadderItem[][] => {
    const result = balances.map((balance) => balance.map((item) => ({ ...item })));
    orders.forEach(({ currency, valueDate, nettedOrderValue }) => {
        if (nettedOrderValue === 0) return;
        let currencyBalance = result.find((balance) => balance.length > 0 && balance[0].currency === currency);
        if (!currencyBalance) {
            if (result.length === 0) return;
            currencyBalance = result[0].map((item) => ({ ...item, name: currency, currency, balance: 0 }));
            result.push(currencyBalance);
        }
        currencyBalance.forEach((item) => {
            if (item.valueDate >= valueDate) {
                item.balance = item.balance + nettedOrderValue;
            }
        });
    });
    return result;
};

export interface CashAccountBalances {
    clientId: string;
    instrumentId: string;
    name: string;
    currency: string;
    dates: string[];
    balances: number[];
    // The fund's main account default cash instrument for this currency
    isDefaultCash?: boolean;
}

export interface AccountBalancesWithOrders {
    clientId: string;
    currency: string;
    // null when the fund has no cash account in the currency
    instrumentId: string | null;
    // undefined on dates without a cash movement or order value date, like the Dashboard without orders
    balances: (number | undefined)[];
}

// Cash ladder dates only contain days with cash movements, so the balance on a day is the last balance on or before it
export const balanceOnDate = (dates: string[], balances: number[], date: string): number => {
    let balance = 0;
    let latestDate = "";
    dates.forEach((d, i) => {
        if (d <= date && d >= latestDate) {
            latestDate = d;
            balance = balances[i];
        }
    });
    return balance;
};

// Adds each fund's netted orders to its main account default cash instrument in the order currency (or else its first
// cash account in that currency), on and after the order's value date. A balance is only shown on dates where it
// changes, i.e. dates with a cash movement or an order value date.
export const addOrdersToAccountBalances = (
    cashAccounts: CashAccountBalances[],
    ordersPerClientId: Record<string, NettedOrderValue[]>,
    dates: string[]
): AccountBalancesWithOrders[] => {
    const rows: (AccountBalancesWithOrders & { runningBalances: number[]; movementDates: Set<string> })[] = [];
    Object.entries(ordersPerClientId).forEach(([clientId, orders]) => {
        orders.forEach(({ currency, valueDate, nettedOrderValue }) => {
            if (nettedOrderValue === 0) return;
            let row = rows.find((r) => r.clientId === clientId && r.currency === currency);
            if (!row) {
                const currencyAccounts = cashAccounts.filter((a) => a.clientId === clientId && a.currency === currency);
                const account = currencyAccounts.find((a) => a.isDefaultCash) ?? currencyAccounts[0];
                row = {
                    clientId,
                    currency,
                    instrumentId: account ? account.instrumentId : null,
                    balances: [],
                    runningBalances: dates.map((date) => (account ? balanceOnDate(account.dates, account.balances, date) : 0)),
                    movementDates: new Set(account ? account.dates : [])
                };
                rows.push(row);
            }
            row.movementDates.add(valueDate);
            dates.forEach((date, i) => {
                if (date >= valueDate) {
                    row.runningBalances[i] = row.runningBalances[i] + nettedOrderValue;
                }
            });
        });
    });
    return rows.map(({ clientId, currency, instrumentId, runningBalances, movementDates }) => ({
        clientId,
        currency,
        instrumentId,
        balances: dates.map((date, i) => (movementDates.has(date) ? runningBalances[i] : undefined))
    }));
};

export interface OrdersInBaseCurrency {
    total: number;
    missingFxCurrencies: string[];
}

// fxRates converts one unit of a currency to the fund's base currency. Orders in currencies without a rate are left out.
export const nettedOrdersInBaseCurrency = (
    orders: NettedOrderValue[],
    baseCurrency: string,
    fxRates: Record<string, number>
): OrdersInBaseCurrency => {
    let total = 0;
    const missingFxCurrencies: string[] = [];
    orders.forEach(({ currency, nettedOrderValue }) => {
        let fxRate = fxRates[currency];
        if (currency === baseCurrency) {
            fxRate = 1;
        }
        if (typeof fxRate === "undefined" || fxRate === null) {
            if (!missingFxCurrencies.includes(currency)) {
                missingFxCurrencies.push(currency);
            }
            return;
        }
        total = total + nettedOrderValue * fxRate;
    });
    return { total, missingFxCurrencies };
};

// Rounds down to whole millions, so a redemption of 4.3 M becomes -5 M and a subscription of 12.7 M becomes 12 M
export const toWholeMillionsRoundedDown = (amount: number): number => {
    const millions = Math.floor(amount / 1e6);
    return millions === 0 ? 0 : millions;
};
